import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Avatar, 
  CircularProgress, 
  Paper, 
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Badge,
  Alert
} from '@mui/material';
import { 
  Send as SendIcon,
  Comment as CommentIcon,
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

import { useAuth } from '../../../contexts/AuthContext';
import { 
  PhotoComment, 
  getPhotoComments, 
  addPhotoComment, 
  toggleCommentLike
} from '../../../services/photoCommentService';

interface TimelinePhotoCommentsProps {
  photoId: string;
  tripId: string;
  maxComments?: number;
  compact?: boolean;
}

const TimelinePhotoComments: React.FC<TimelinePhotoCommentsProps> = ({ 
  photoId, 
  tripId, 
  maxComments = 3,
  compact = false
}) => {
  const { currentUser } = useAuth();
  const [comments, setComments] = useState<PhotoComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [showAllComments, setShowAllComments] = useState(false);

  useEffect(() => {
    const loadComments = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchedComments = await getPhotoComments(photoId);
        setComments(fetchedComments);
      } catch (err) {
        console.error('Error loading comments:', err);
        setError('Failed to load comments');
      } finally {
        setLoading(false);
      }
    };

    if (photoId) {
      loadComments();
    }
  }, [photoId]);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!commentText.trim() || !currentUser) return;

    try {
      setSubmitting(true);
      const newComment = await addPhotoComment({
        photoId,
        tripId,
        text: commentText.trim()
      });

      setComments(prevComments => [...prevComments, newComment]);
      setCommentText('');
    } catch (err: any) {
      setError(err.message || 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLikeComment = async (commentId: string) => {
    if (!currentUser) return;
    
    try {
      await toggleCommentLike(commentId);
      
      setComments(prevComments => 
        prevComments.map(comment => {
          if (comment.id === commentId) {
            const userLiked = comment.likes.includes(currentUser.uid);
            return {
              ...comment,
              likes: userLiked
                ? comment.likes.filter(uid => uid !== currentUser.uid)
                : [...comment.likes, currentUser.uid]
            };
          }
          return comment;
        })
      );
    } catch (err: any) {
      console.error('Error liking comment:', err);
    }
  };

  const visibleComments = showAllComments 
    ? comments 
    : comments.slice(Math.max(0, comments.length - maxComments));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (compact) {
    return (
      <Badge 
        badgeContent={comments.length} 
        color="primary"
        sx={{ mr: 1 }}
        invisible={comments.length === 0}
      >
        <CommentIcon color="action" />
      </Badge>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {comments.length > 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1">
              Comments ({comments.length})
            </Typography>
            
            {comments.length > maxComments && (
              <Button 
                size="small" 
                onClick={() => setShowAllComments(!showAllComments)}
              >
                {showAllComments ? 'Show Less' : 'Show All'}
              </Button>
            )}
          </Box>

          <List sx={{ p: 0 }}>
            {visibleComments.map(comment => (
              <Paper key={comment.id} sx={{ mb: 1, p: 1 }}>
                <ListItem alignItems="flex-start" sx={{ px: 1, py: 0.5 }}>
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar 
                      src={comment.userPhotoURL} 
                      alt={comment.userDisplayName}
                      sx={{ width: 32, height: 32 }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle2">
                          {comment.userDisplayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" color="text.primary" sx={{ mt: 0.5, mb: 1 }}>
                          {comment.text}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IconButton 
                            size="small" 
                            onClick={() => handleLikeComment(comment.id)}
                            color={currentUser && comment.likes.includes(currentUser.uid) ? 'primary' : 'default'}
                          >
                            {currentUser && comment.likes.includes(currentUser.uid) 
                              ? <ThumbUpIcon fontSize="small" /> 
                              : <ThumbUpOutlinedIcon fontSize="small" />
                            }
                          </IconButton>
                          {comment.likes.length > 0 && (
                            <Typography variant="caption" sx={{ ml: 0.5 }}>
                              {comment.likes.length}
                            </Typography>
                          )}
                        </Box>
                      </>
                    }
                  />
                </ListItem>
              </Paper>
            ))}
          </List>
        </>
      )}

      {/* Comment Form */}
      {currentUser && (
        <Box component="form" onSubmit={handleSubmitComment} sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Avatar 
              src={currentUser.photoURL || undefined} 
              alt={currentUser.displayName || 'User'}
              sx={{ width: 32, height: 32 }}
            />
            <TextField
              fullWidth
              placeholder="Add a comment..."
              size="small"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              disabled={submitting}
            />
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={submitting || !commentText.trim()}
            >
              {submitting ? <CircularProgress size={24} /> : <SendIcon />}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TimelinePhotoComments; 